import {
  BODY_TEXT,
  BORDER,
  BRIGHT_BLUE,
  DARK_GREY,
  DEEP_BLUE,
  DIVIDER,
  GREY,
  LIGHT_RED,
  OPAQUE_GREEN,
  OPAQUE_YELLOW,
  SCOUT_BLUE,
  SCOUT_TEAL,
  SUCCESS_GREEN,
  WHITE,
  YELLOW
} from '../../ThemeContext/constants';
import { theme } from '../../ThemeContext/ThemeObject';
import { actionsMenuPaperCommonStyles } from '../shared/commonStyles';
import { emptyStateText as sharedEmptyStateText } from '../shared/styleClasses';

// The contact details stack under the identity when the header container is
// narrow (the app/interview sidebars shrink it independently of the viewport), or
// whenever the viewport is 900px or less. The page drops its side padding at that
// viewport width (admin/new_layout_overrides.scss), which widens the header; a
// container query alone would flip back to one row just below 900px.
const HEADER_NARROW_CONTAINER = '@container application-header (max-width: 720px)';
const HEADER_NARROW_VIEWPORT = '@media (max-width: 900px)';

const stackedHeaderContactGroup = {
  order: 1,
  flexBasis: '100%',
  flexDirection: 'column',
  rowGap: 2,
  '& > .MuiStack-root:first-of-type': { maxWidth: 'none' }
} as const;

export const styles = {
  applicationStatusDropdownStyle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '6px',
    color: BODY_TEXT,
    border: `1px solid ${DIVIDER}`,
    fontSize: '12px',
    marginTop: 1,
    padding: '6px 10px'
  },
  applicationStatusPopoverStyle: {
    '& .MuiPaper-root': {
      minWidth: '150px',
      maxHeight: '200px',
      overflow: 'auto',
      borderRadius: '6px',
      marginTop: 1
    }
  },
  actionsMenu: {
    '& .MuiPaper-root': {
      ...actionsMenuPaperCommonStyles,
      rowGap: '11px',
      minWidth: '153px',
      marginTop: '6px'
    }
  },
  actionsItem: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 'bold',
    color: GREY,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  linkedInURLInput: {
    '.MuiInputBase-input': {
      height: '10px',
      margin: 'unset',
      padding: '4px 6px',
      fontSize: '11px',
      minWidth: '150px',
      borderRadius: '4px',
      '&::placeholder': {
        fontStyle: 'italic'
      }
    }
  },
  timeStampInput: {
    '.MuiInputBase-input': {
      height: '16px',
      padding: '4px 6px',
      fontSize: '11px',
      minWidth: '50px',
      borderRadius: '4px',
      '&::placeholder': {
        fontStyle: 'italic'
      }
    }
  },
  timeStampItem: {
    cursor: 'pointer',
    color: theme.palette.primary.dark,
    fontFamily: 'Source Sans Pro',
    fontWeight: 600,
    height: '16px',
    margin: 'unset',
    padding: '4px 6px',
    fontSize: '14px',
    minWidth: '50px'
  },
  linkedInURLLink: {
    cursor: 'pointer',
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: SCOUT_BLUE,
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    '&:hover': { textDecoration: 'underline' }
  },
  modalTitle: {
    fontWeight: 'bold',
    color: SCOUT_BLUE,
    fontSize: '25px'
  },
  clickableGreyIcon: {
    cursor: 'pointer',
    color: DARK_GREY
  },
  scoutNavyIcon: {
    color: SCOUT_BLUE,
    fontSize: '20px'
  },
  smallGreyIcon: {
    cursor: 'pointer',
    color: DARK_GREY,
    fontSize: '16px'
  },
  applicationHeaderItems: {
    flexDirection: 'row',
    columnGap: 1,
    alignItems: 'center',
    minWidth: 0,
    fontSize: '14px',
    color: BODY_TEXT,
    '& > svg': { flexShrink: 0 }
  },
  // Wraps onto a second line, then clamps, so long contact details stay readable
  // without growing the sticky header by more than one line.
  applicationHeaderText: {
    minWidth: 0,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    overflowWrap: 'anywhere'
  },
  // The header stays one row and the contact details wrap within their columns as
  // it narrows, instead of the whole group dropping below the identity and growing
  // the sticky header. The contact group's zero flex-basis keeps its full text
  // width from forcing an early wrap.
  applicationHeaderRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 3,
    rowGap: 2,
    containerType: 'inline-size',
    containerName: 'application-header',
    [HEADER_NARROW_CONTAINER]: { '& > .MuiDivider-root': { display: 'none' } },
    [HEADER_NARROW_VIEWPORT]: { '& > .MuiDivider-root': { display: 'none' } }
  },
  // Flex wraps by each item's full width before anything shrinks, so a long name
  // narrows here or the actions would drop to a second row just above 720px.
  applicationHeaderIdentity: {
    flex: '0 0 auto',
    minWidth: '150px',
    maxWidth: '280px',
    '@container application-header (max-width: 820px)': { maxWidth: '180px' }
  },
  // The address/date column gives up width first; the email column keeps its
  // text until it would take more than half the group.
  applicationHeaderContactAddressGroup: {
    flexDirection: 'row',
    flex: '1 1 0',
    columnGap: 3,
    minWidth: '260px',
    '& > .MuiStack-root': { flex: '0 1 auto', minWidth: '120px', rowGap: 2, paddingTop: 0.5 },
    '& > .MuiStack-root:first-of-type': { flex: '0 0 auto', maxWidth: '50%' },
    [HEADER_NARROW_CONTAINER]: stackedHeaderContactGroup,
    [HEADER_NARROW_VIEWPORT]: stackedHeaderContactGroup
  },
  applicationHeaderActionsContainer: {
    flexDirection: 'row',
    flexShrink: 0,
    columnGap: 2,
    marginLeft: 'auto'
  },
  surroundingApplications: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: '8px',
    columnGap: 1,
    color: GREY
  },
  surroundingApplicationsButton: {
    border: `1px solid ${BORDER}`,
    borderRadius: '6px',
    padding: '2px 6px',
    cursor: 'pointer'
  },
  additionalInfoContainer: {
    padding: '24px 0px',
    borderRadius: '6px',
    border: `1px solid ${BORDER}`,
    flexDirection: 'row',
    columnGap: 2,
    justifyContent: 'space-around'
  },
  AISummaryContainer: {
    padding: 3,
    borderRadius: '6px',
    border: `1px solid ${BORDER}`,
    rowGap: 1
  },
  AISummaryHeader: {
    svg: { width: '40px', height: '40px' },
    flexDirection: 'row',
    columnGap: 1,
    alignItems: 'center'
  },
  AISummaryMessage: {
    fontWeight: 600,
    color: DEEP_BLUE,
    span: { color: '#AFBCFF' }
  },
  AISummaryButton: {
    background: BRIGHT_BLUE,
    color: 'white',
    padding: '6px 24px',
    borderRadius: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  generateSummaryButton: {
    flexDirection: 'row',
    color: '#AFBCFF',
    padding: '6px 16px',
    borderRadius: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    border: '2px solid #AFBCFF',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '170px'
  },
  summaryLastUpdated: {
    flexDirection: 'row',
    columnGap: '4px',
    alignItems: 'center',
    color: GREY,
    fontStyle: 'italic',
    fontSize: '12px'
  },
  ratingCell: {
    textAlign: 'center',
    padding: '4px 12px',
    borderRadius: '4px',
    width: '60px',
    fontWeight: 'bold'
  },
  ratingInfoContainer: {
    padding: '6px 16px',
    margin: 1,
    border: `1px solid ${SCOUT_TEAL}`,
    borderRadius: '10px'
  },
  ratingInfoClickable: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    svg: { color: SCOUT_TEAL },
    cursor: 'pointer'
  },
  ratingButton: {
    border: `1px solid ${BORDER}`,
    borderRadius: '3px',
    width: '30px',
    height: '30px',
    padding: 0,
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
    color: GREY,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1
  },
  ratingColor: {
    1: {
      color: '#E37D7A',
      background: LIGHT_RED,
      borderColor: LIGHT_RED
    },
    2: {
      color: '#E58F66',
      background: 'rgba(229, 143, 102, 0.12)',
      borderColor: 'rgba(229, 143, 102, 0.12)'
    },
    3: {
      color: YELLOW,
      background: OPAQUE_YELLOW,
      borderColor: OPAQUE_YELLOW
    },
    4: {
      color: '#9BB974',
      background: 'rgba(155, 185, 116, 0.12)',
      borderColor: 'rgba(155, 185, 116, 0.12)'
    },
    5: {
      color: SUCCESS_GREEN,
      background: OPAQUE_GREEN,
      borderColor: OPAQUE_GREEN
    }
  },
  questionnaireContainer: {
    padding: 3,
    borderRadius: '6px',
    border: `1px solid ${BORDER}`,
    rowGap: 2
  },
  attachmentsContainer: {
    padding: 3,
    borderRadius: '6px',
    border: `1px solid ${BORDER}`,
    rowGap: 2
  },
  ratingCard: {
    flexDirection: 'row',
    fontSize: '14px',
    borderRadius: '3px',
    flex: '1 1 0%',
    minWidth: 0,
    padding: '8px 16px',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    svg: { fontSize: '14px' },
    '@media (max-width: 1450px)': {
      padding: '8px 10px',
      fontSize: '13px'
    },
    '@media (max-width: 1260px)': {
      padding: '6px 8px',
      fontSize: '11px'
    }
  },
  ratingValue: {
    fontWeight: 'bold',
    fontSize: '12px'
  },
  editRating: {
    '.MuiInputBase-input': {
      height: '8px',
      borderRadius: '3px',
      padding: 0.5,
      fontSize: '12px',
      fontWeight: 'bold',
      margin: 'unset'
    },
    '& input::placeholder': {
      fontSize: '10px'
    }
  },
  editableRatings: {
    '.MuiFormControl-root': { width: '45px' },
    '.MuiStack-root': { marginTop: 'unset' }
  },
  sortableSectionsButton: {
    border: '1px solid #EAEEF0',
    borderRadius: '6px',
    padding: '6px 14px',
    height: 'min-content',
    cursor: 'pointer',
    '& svg': {
      fontSize: '30px',
      color: SCOUT_BLUE
    },
    '&:hover': {
      backgroundColor: 'rgba(8,77,109,0.121)'
    }
  },
  sortableSectionsButtonOpen: {
    backgroundColor: 'rgba(8,77,109,0.121)'
  },
  sortableSectionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 1
  },
  timeslotAvailabilityButton: {
    padding: '6px 24px',
    borderRadius: '6px',
    alignItems: 'center',
    fontWeight: 'bold',
    width: 'min-content'
  },
  preview: {
    color: DARK_GREY,
    fontSize: 'medium',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  fileIconContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  fileIcon: {
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '10px',
    backgroundColor: `${SCOUT_BLUE}10`,
    '& svg': {
      color: theme.palette.primary.main,
      margin: '0px 2px 2px 0px'
    }
  },
  fileName: {
    color: theme.palette.primary.main,
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    overflowWrap: 'normal',
    maxWidth: 'calc(100% - 60px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical'
  },
  fileDate: {
    color: '#999999'
  },
  fileAttachedBy: {
    color: BODY_TEXT,
    fontSize: '15px'
  },
  noAttachments: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    margin: '24px 0px'
  },
  tableWrapper: {
    tableLayout: 'auto !important',
    border: 'none',
    'td, th, tr, thead': {
      fontFamily: 'Source Sans Pro, sans-serif',
      border: 'none',
      backgroundColor: WHITE,
      textTransform: 'none',
      padding: '12px 0px',
      cursor: 'inherit'
    },
    th: {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      paddingBottom: '16px',
      '& .Mui-active': {
        color: theme.palette.primary.main
      }
    }
  },
  tableActionIcon: {
    '&:focus': {
      outline: 'none'
    }
  },
  timeslotsContainer: {
    flexDirection: 'row',
    width: '100%',
    maxHeight: '290px',
    columnGap: 2,
    rowGap: 2,
    flexFlow: 'wrap',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  timeslot: {
    border: `1px solid ${DIVIDER}`,
    borderRadius: '6px',
    padding: '8px 16px',
    fontWeight: 'bold',
    width: '125px'
  },
  timeslotSubject: {
    fontSize: '12px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  noTimeslotMessage: {
    padding: 9,
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.palette.common.grey
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    margin: '100px 0px'
  },
  emptyStateText: {
    ...sharedEmptyStateText,
    margin: '32px 0px'
  },
  applicationPageStickyHeader: {
    position: 'sticky',
    top: '0px',
    zIndex: 10,
    background: 'white',
    rowGap: 1,
    paddingBottom: 3
  },
  emptyStateIcon: { fontSize: '5rem', color: '#EAEAEA' },
  emptyStateMessage: { color: DIVIDER, fontWeight: 'bold' },
  smartFormsAnswersPreviewContainer: {
    '& > div > div:first-of-type': {
      color: theme.palette.primary.dark,
      fontWeight: 'bold'
    },
    '& > div > div:not(:first-of-type)': {
      color: BODY_TEXT
    },
    '& > div': {
      paddingLeft: 0
    }
  },
  smartFormsTableContainer: {
    tableLayout: 'auto !important',
    border: 'none',
    th: {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      paddingBottom: '16px',
      '& .Mui-active': {
        color: theme.palette.primary.main
      }
    },
    'td, th, tr, thead': {
      fontFamily: 'Source Sans Pro, sans-serif',
      border: 'none',
      backgroundColor: WHITE,
      textTransform: 'none',
      padding: '24px'
    },
    '& thead > tr': { borderBottom: '1px solid #E5E5E5' },
    '& tbody > tr:nth-of-type(2n)': {
      borderBottom: '1px solid #E5E5E5'
    }
  },
  ratingInput: { flexDirection: 'row', alignItems: 'center', columnGap: 0.5 },
  questionsAndRatingsContainer: {
    overflow: 'auto',
    rowGap: 2,
    flexGrow: 1
  },
  betaButtonTooltipContainer: {
    width: '430px',
    height: '400px',
    color: BODY_TEXT,
    fontFamily: 'Source Sans Pro',
    rowGap: 2,
    padding: 2
  },
  candidateLink: {
    color: SCOUT_BLUE,
    fontSize: '20px',
    fontWeight: 'bold',
    width: 'fit-content'
  }
};
